import { useEffect, useCallback, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/components/ui/Popover";
import { Button } from "@ui/components/ui/Button";
import { Calendar } from "@ui/components/ui/Calendar";
import { observer } from "mobx-react-lite";
import { DateRange } from "react-day-picker";
import { Dates } from "@ac-expo/api-types";
import {
  addDays,
  endOfMonth,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  nextDay,
} from "date-fns";
import filterStore from "../../services/FilterStore";
import { TrashIcon } from "@web-app/src/icons";

const radioItems = [
  { label: "Demain", value: Dates.TOMORROW },
  { label: "Cette semaine", value: Dates.THIS_WEEK },
  { label: "Ce weekend", value: Dates.THIS_WEEKEND },
  { label: "Ce mois", value: Dates.THIS_MONTH },
];

const getSelectedDateLabel = () => {
  const selectedItem = radioItems.find(
    (item) => item.value === filterStore.selectedDateOption
  );

  if (filterStore.selectedDateOption === Dates.CUSTOM) {
    return ` - ${filterStore.startDate?.toLocaleDateString()} - ${filterStore.endDate?.toLocaleDateString()}`;
  }
  return selectedItem ? ` - ${selectedItem.label}` : "";
};

const DateFilter = observer(
  ({
    startDate,
    endDate,
  }: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  }) => {
    // This is needed to make calendar rerender on click of buttons
    const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(
      filterStore.rangeDate?.from
    );

    const handleDateChange = useCallback(
      (newStartDate: Date | undefined, newEndDate: Date | undefined) => {
        filterStore.setStartDate(newStartDate);
        filterStore.setEndDate(newEndDate);
        setCalendarMonth(newEndDate);
      },
      []
    );

    function handleResetFilters() {
      filterStore.setRangeDate({ from: undefined, to: undefined });
      filterStore.setSelectedDateOption(Dates.DEFAULT);
    }

    const handleSelectOption = useCallback((option: Dates) => {
      if (filterStore.selectedDateOption === option) {
        filterStore.setSelectedDateOption(Dates.DEFAULT);
        filterStore.setRangeDate({ from: undefined, to: undefined });
      } else {
        filterStore.setSelectedDateOption(option);
        const today = new Date();
        if (option === Dates.TOMORROW) {
          const date = addDays(today, 1);
          filterStore.setRangeDate({ from: date, to: date });
          setCalendarMonth(date);
        } else if (option === Dates.THIS_WEEK) {
          const start = startOfWeek(today, { weekStartsOn: 1 });
          const end = endOfWeek(today, { weekStartsOn: 1 });
          filterStore.setRangeDate({ from: start, to: end });
        } else if (option === Dates.THIS_WEEKEND) {
          const end = today.getDay() === 0 ? today : nextDay(today, 0);
          const start = addDays(end, -1);
          filterStore.setRangeDate({ from: start, to: end });
        } else if (option === Dates.THIS_MONTH) {
          const start = startOfMonth(today);
          const end = endOfMonth(today);
          filterStore.setRangeDate({ from: start, to: end });
        }
      }
    }, []);

    const handleSelectRange = (range: DateRange | undefined) => {
      const from = range?.from || range?.to;
      const to = range?.to || from;

      if (from === undefined && to === undefined) {
        filterStore.setRangeDate({ from: undefined, to: undefined });
        filterStore.setSelectedDateOption(Dates.DEFAULT);
        return;
      }

      filterStore.setRangeDate({ from, to });
      filterStore.setSelectedDateOption(Dates.CUSTOM);

      handleDateChange(from, to);
    };

    useEffect(() => {
      const newStartDate = filterStore.rangeDate?.from || undefined;
      const newEndDate = filterStore.rangeDate?.to || undefined;
      if (startDate !== newStartDate || endDate !== newStartDate) {
        handleDateChange(newStartDate, newEndDate);
      }
    }, [startDate, endDate]);

    return (
      <Popover>
        <div className="flex w-full flex-row items-center gap-2">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-[41px] w-full justify-between bg-white font-normal text-gray-500"
            >
              Dates{getSelectedDateLabel()}
            </Button>
          </PopoverTrigger>
          {(filterStore.rangeDate?.from !== undefined ||
            filterStore.rangeDate?.to !== undefined) && (
            <Button
              onClick={handleResetFilters}
              variant="ghost"
              aria-label="Réinitialiser les filtres"
              className="self-center p-0 px-1"
            >
              <TrashIcon height={15} width={15} fill={"#344054"} />
            </Button>
          )}
        </div>
        <PopoverContent className="w-96 p-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              {radioItems.map((item) => (
                <Button
                  key={item.value}
                  variant={
                    filterStore.selectedDateOption === item.value
                      ? "blue"
                      : "outline"
                  }
                  onClick={() => handleSelectOption(item.value)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <div className="h-[523px] w-[347px] gap-4 space-y-2 rounded-s-lg border p-4">
              <h4 className="font-medium leading-none">
                Sélectionner une date
              </h4>

              <div className="flex h-[47px] w-full gap-4">
                <div className="flex flex-col gap-1">
                  <p className="font-nunito text-xs leading-4 text-gray-500">
                    Date de début
                  </p>
                  <p className="text-sm font-semibold">
                    {startDate instanceof Date
                      ? startDate.toLocaleDateString()
                      : "Sélectionner"}
                  </p>
                </div>
                <div className="ml-auto flex flex-col gap-1 ">
                  <p className="font-nunito text-xs leading-4 text-gray-500">
                    Date de fin
                  </p>
                  <p className="text-sm font-semibold">
                    {endDate instanceof Date
                      ? endDate.toLocaleDateString()
                      : "Sélectionner"}
                  </p>
                </div>
              </div>

              <div className="h-[382px] w-full rounded-lg border border-gray-200 shadow-xl">
                <div className="h-[352px] w-full gap-3 p-7">
                  <Calendar
                    mode="range"
                    selected={filterStore.rangeDate}
                    onSelect={handleSelectRange}
                    defaultMonth={calendarMonth}
                    month={calendarMonth}
                    onMonthChange={(month) => setCalendarMonth(month)}
                    disabled={[{ before: new Date() }]}
                  />
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

export default DateFilter;
